"use client";

import { useEffect, useRef } from "react";

interface ParsedKeys {
  // Either a single chord or a sequence of single keys
  type: "chord" | "sequence";
  // For chord: { key: 'k', cmd: true }
  // For sequence: array of single keys ['g', 'a']
  parts: string[];
  modifiers: { cmd: boolean; shift: boolean; alt: boolean };
}

const SEQUENCE_TIMEOUT_MS = 1500;

const SHIFT_GLYPHS = new Set([
  "?",
  "!",
  "@",
  "#",
  "$",
  "%",
  "^",
  "&",
  "*",
  "(",
  ")",
  "_",
  "+",
  "{",
  "}",
  "|",
  ":",
  '"',
  "<",
  ">",
  "~",
]);

function requiresShift(key: string): boolean {
  return SHIFT_GLYPHS.has(key);
}

function parseKeys(spec: string): ParsedKeys {
  if (spec.includes("+")) {
    const tokens = spec.split("+").map((t) => t.trim());
    const last = tokens.pop();
    if (!last) throw new Error(`Invalid hotkey "${spec}"`);
    const modifiers = {
      cmd: tokens.includes("Cmd") || tokens.includes("Ctrl"),
      shift: tokens.includes("Shift"),
      alt: tokens.includes("Alt"),
    };
    return { type: "chord", parts: [last.toLowerCase()], modifiers };
  }
  const parts = spec.split(/\s+/).map((p) => p.toLowerCase()).filter(Boolean);
  if (parts.length === 0) throw new Error(`Invalid hotkey "${spec}"`);
  if (parts.length === 1) {
    return { type: "chord", parts, modifiers: { cmd: false, shift: false, alt: false } };
  }
  return { type: "sequence", parts, modifiers: { cmd: false, shift: false, alt: false } };
}

function shouldIgnore(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  if (target.isContentEditable) return true;
  return false;
}

export function useHotkey(keys: string, handler: () => void): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const parsed = parseKeys(keys);
    const seq: string[] = [];
    let lastSeqAt = 0;

    const onKey = (e: KeyboardEvent) => {
      if (shouldIgnore(e.target)) return;

      if (parsed.type === "chord") {
        const wantCmd = parsed.modifiers.cmd;
        const wantShift = parsed.modifiers.shift;
        const wantAlt = parsed.modifiers.alt;
        const cmdPressed = e.metaKey || e.ctrlKey;
        if (wantCmd !== cmdPressed) return;
        // Allow shift to be implicit when the target key is a shift-only glyph
        const expectedKey = parsed.parts[0];
        const expectShift =
          wantShift || (expectedKey !== undefined && requiresShift(expectedKey));
        if (expectShift !== e.shiftKey) return;
        if (wantAlt !== e.altKey) return;
        if (e.key.toLowerCase() === expectedKey?.toLowerCase()) {
          e.preventDefault();
          handlerRef.current();
        }
        return;
      }

      // sequence
      const now = Date.now();
      if (now - lastSeqAt > SEQUENCE_TIMEOUT_MS) seq.length = 0;
      lastSeqAt = now;
      seq.push(e.key.toLowerCase());
      const target = parsed.parts;
      // Match the tail of seq against target
      if (seq.length >= target.length) {
        const tail = seq.slice(seq.length - target.length);
        if (tail.every((k, i) => k === target[i])) {
          e.preventDefault();
          handlerRef.current();
          seq.length = 0;
        }
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [keys]);
}
